/**
 * Configuración → Automatización de RIC (`/configuracion/ric-automation`).
 *
 * Puerto de `Settings::ric_automation()` + `Settings::get_ric_configs()` +
 * `Settings::save_ric_config()` + `views/settings/ric_automation.php`: una tarjeta
 * por tanque con la regla de toma automática de medidas (activo/inactivo, tipo de
 * frecuencia, cada cuántas horas o una lista de horarios, y la última ejecución).
 *
 * Reglas conservadas del panel anterior:
 * - `hourly` («Cada N horas») usa el campo de horas (1 a 24); `daily` y `custom`
 *   («Personalizado») usan la lista de horarios. El panel anterior mostraba los
 *   mismos campos para los dos últimos, así que aquí también.
 * - Un tanque **activo** con horarios (no `hourly`) necesita al menos uno; el
 *   aviso es el mismo: «Debe agregar al menos un horario para la automatización».
 * - La última ejecución es de sólo lectura: la escribe quien ejecuta la toma.
 * - Pide el grupo **admin**, como `in_group(1)` en el panel anterior.
 *
 * Aviso: la ejecución automática (el cron que leería esta configuración) todavía
 * no existe —las medidas de RIC se capturan a mano—, así que la pantalla lo dice
 * explícitamente para que nadie crea que la toma ya es automática.
 */

import {
  ArrowLeft,
  Bot,
  CalendarClock,
  Clock,
  Cylinder,
  Plus,
  Save,
  ShieldAlert,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select } from '@/components/ui/select';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { ApiError } from '@/lib/http/api-error';
import { cn, formatDateTimeValue } from '@/lib/utils';

import { useMutarRic, useRicAutomation } from '../avanzado.api';
import type { RicPayload, TanqueRic, TipoFrecuenciaRic } from '../avanzado.types';

const CONTROL =
  'h-10 w-full rounded-control border border-ink-200 bg-white px-2.5 text-sm text-ink-800 ' +
  'transition-colors focus:border-brand-400 focus:ring-4 focus:ring-brand-100 focus:outline-none';

/** Mensaje legible de un error (de la API o de red). */
function mensajeDe(error: unknown, porDefecto: string): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;

  return porDefecto;
}

/** La regla con la que se compara el formulario para saber si hay cambios. */
function instantanea(config: TanqueRic['config']): string {
  return JSON.stringify({
    activo: config.activo,
    tipo_frecuencia: config.tipo_frecuencia,
    valor_frecuencia: config.valor_frecuencia,
    horarios: [...config.horarios].sort(),
  });
}

/** Tarjeta de un tanque: el formulario completo de su regla. */
function TanqueTarjeta({
  tanque,
  tipos,
  guardando,
  onGuardar,
}: {
  tanque: TanqueRic;
  tipos: { valor: TipoFrecuenciaRic; etiqueta: string }[];
  guardando: boolean;
  onGuardar: (idTanque: number, payload: RicPayload) => Promise<void>;
}) {
  const [activo, setActivo] = useState(tanque.config.activo);
  const [tipo, setTipo] = useState<TipoFrecuenciaRic>(tanque.config.tipo_frecuencia);
  const [horas, setHoras] = useState(String(tanque.config.valor_frecuencia));
  const [horarios, setHorarios] = useState<string[]>(tanque.config.horarios);
  const [nuevaHora, setNuevaHora] = useState('08:00');
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const usaHorarios = tipo !== 'hourly';
  const cambios =
    instantanea(tanque.config) !==
    JSON.stringify({
      activo,
      tipo_frecuencia: tipo,
      valor_frecuencia: Number(horas) || 0,
      horarios: [...horarios].sort(),
    });
  const faltaHorario = activo && usaHorarios && horarios.length === 0;

  const agregarHora = () => {
    setAviso(null);
    const hora = nuevaHora.trim();

    if (horarios.includes(hora)) {
      setAviso(`El horario ${hora} ya está en la lista.`);
      return;
    }

    setHorarios((actuales) => [...actuales, hora].sort());
  };

  const guardar = async () => {
    setError(null);
    setAviso(null);

    try {
      await onGuardar(tanque.id_tanque, {
        activo,
        tipo_frecuencia: tipo,
        valor_frecuencia: Number(horas) || 24,
        horarios: usaHorarios ? horarios : [],
      });
    } catch (fallo) {
      setError(mensajeDe(fallo, 'No se pudo guardar la configuración.'));
    }
  };

  return (
    <Card className={cn('flex flex-col', activo && 'ring-1 ring-emerald-100')}>
      <div
        className={cn(
          'flex items-start justify-between gap-3 rounded-t-card border-b px-4 py-3',
          activo ? 'border-emerald-100 bg-emerald-50/60' : 'border-ink-100 bg-ink-50/60',
        )}
      >
        <span className="flex flex-wrap items-center gap-2">
          <span
            aria-hidden="true"
            className="size-3.5 shrink-0 rounded-full ring-1 ring-ink-200"
            style={{ backgroundColor: tanque.color ?? '#e5e7eb' }}
          />
          <span>
            <span className="flex items-center gap-2 text-sm font-semibold text-ink-900">
              <Cylinder className="size-4 text-ink-400" aria-hidden="true" />
              Tanque {tanque.id_tanque}
              <Badge variant={activo ? 'success' : 'neutral'}>
                {activo ? 'Activo' : 'Desactivado'}
              </Badge>
            </span>
            <span className="block text-xs text-ink-500">{tanque.producto ?? 'sin producto'}</span>
          </span>
        </span>

        <Checkbox
          label="Activar"
          checked={activo}
          onChange={(evento) => setActivo(evento.target.checked)}
        />
      </div>

      <CardContent className="flex flex-1 flex-col gap-4 pt-4">
        {error ? <Alert variant="error" title="No se pudo guardar">{error}</Alert> : null}
        {aviso ? <Alert variant="warning">{aviso}</Alert> : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor={`ric-tipo-${tanque.id_tanque}`} className="text-sm font-medium text-ink-700">
            Tipo de frecuencia
          </label>
          <Select
            id={`ric-tipo-${tanque.id_tanque}`}
            className={CONTROL}
            value={tipo}
            onChange={(evento) => setTipo(evento.target.value as TipoFrecuenciaRic)}
          >
            {tipos.map((opcion) => (
              <option key={opcion.valor} value={opcion.valor}>
                {opcion.etiqueta}
              </option>
            ))}
          </Select>
        </div>

        {usaHorarios ? (
          <div className="flex flex-col gap-3">
            <span className="flex items-center gap-2 text-sm font-medium text-ink-700">
              <CalendarClock className="size-4 text-ink-400" aria-hidden="true" />
              Horarios programados
            </span>

            {horarios.length === 0 ? (
              <p className="rounded-control bg-ink-50 px-3 py-2 text-center text-xs text-ink-400">
                No hay horarios configurados
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {horarios.map((hora) => (
                  <span
                    key={hora}
                    className="flex items-center gap-1.5 rounded-full bg-brand-50 py-1 pl-2.5 pr-1 text-xs font-medium text-brand-700 ring-1 ring-brand-100"
                  >
                    <Clock className="size-3" aria-hidden="true" />
                    {hora}
                    <button
                      type="button"
                      aria-label={`Quitar el horario ${hora}`}
                      title="Quitar"
                      className="rounded-full p-0.5 text-brand-500 transition-colors hover:bg-brand-100"
                      onClick={() =>
                        setHorarios((actuales) => actuales.filter((actual) => actual !== hora))
                      }
                    >
                      <X className="size-3" aria-hidden="true" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            <div className="flex items-end gap-2">
              <Input
                label="Nuevo horario"
                type="time"
                value={nuevaHora}
                containerClassName="flex-1"
                onChange={(evento) => setNuevaHora(evento.target.value)}
              />
              <Button
                variant="outline"
                size="sm"
                className="mb-0.5"
                leftIcon={<Plus className="size-3.5" aria-hidden="true" />}
                onClick={agregarHora}
              >
                Agregar
              </Button>
            </div>
          </div>
        ) : (
          <Input
            label="Frecuencia (horas)"
            type="number"
            min={1}
            max={24}
            value={horas}
            hint="Ejecutar cada cuántas horas"
            onChange={(evento) => setHoras(evento.target.value)}
          />
        )}

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-ink-700">Última ejecución</span>
          <p className="rounded-control bg-ink-50 px-3 py-2 text-xs text-ink-500">
            {tanque.config.ultima_ejecucion
              ? formatDateTimeValue(tanque.config.ultima_ejecucion)
              : 'Nunca ejecutado'}
          </p>
        </div>

        {faltaHorario ? (
          <Alert variant="warning">
            Para activar el tanque agrega al menos un horario, o cambia el tipo a «Cada N horas».
          </Alert>
        ) : null}

        <div className="mt-auto flex items-center justify-between gap-2 border-t border-ink-100 pt-3">
          <span className="text-[11px] text-ink-400">
            {tanque.config.guardado
              ? `Guardado${tanque.config.actualizado_en ? ` · ${formatDateTimeValue(tanque.config.actualizado_en)}` : ''}`
              : 'Sin configuración guardada'}
          </span>
          <span className="flex flex-wrap items-center gap-2">
            {cambios ? (
              <Button
                variant="ghost"
                size="sm"
                disabled={guardando}
                onClick={() => {
                  setActivo(tanque.config.activo);
                  setTipo(tanque.config.tipo_frecuencia);
                  setHoras(String(tanque.config.valor_frecuencia));
                  setHorarios(tanque.config.horarios);
                  setError(null);
                  setAviso(null);
                }}
              >
                Descartar
              </Button>
            ) : null}
            <Button
              size="sm"
              loading={guardando}
              disabled={!cambios || faltaHorario}
              leftIcon={<Save className="size-3.5" aria-hidden="true" />}
              onClick={guardar}
            >
              Guardar
            </Button>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

export function RicAutomationPage() {
  useDocumentTitle('Configuración · Automatización de RIC');

  const ric = useRicAutomation();
  const mutaciones = useMutarRic();

  const [aviso, setAviso] = useState<{ tono: 'success' | 'error'; texto: string } | null>(null);
  const [tanqueGuardando, setTanqueGuardando] = useState<number | null>(null);

  const sinPermiso = ric.error instanceof ApiError && ric.error.status === 403;
  const tanques = ric.data?.tanques ?? [];
  const tipos = ric.data?.tipos_frecuencia ?? [];

  const cabecera = (
    <header className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <Link
          to="/configuracion"
          className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-ink-400 transition-colors hover:text-brand-600"
        >
          <ArrowLeft className="size-3" aria-hidden="true" />
          Configuración
        </Link>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <Bot className="size-6 text-brand-500" aria-hidden="true" />
          Automatización de RIC
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-ink-500">
          Cada cuánto debe tomarse automáticamente la medida de cada tanque. Es la configuración
          que el sistema usa para programar la toma de RIC.
        </p>
      </div>

      {ric.data ? (
        <Badge variant={ric.data.activos > 0 ? 'success' : 'neutral'}>
          {ric.data.activos} de {ric.data.total} tanques activos
        </Badge>
      ) : null}
    </header>
  );

  if (sinPermiso) {
    return (
      <div className="space-y-6">
        {cabecera}
        <Alert variant="warning" title="Acceso restringido">
          <span className={ICONO_EN_ALERTA}>
            <ShieldAlert className="size-4" aria-hidden="true" />
            Esta pantalla es del grupo <strong>Administrador</strong>, como en el panel anterior.
          </span>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {cabecera}

      {aviso ? (
        <Alert variant={aviso.tono === 'success' ? 'success' : 'error'}>{aviso.texto}</Alert>
      ) : null}

      <Alert variant="info" title="La toma automática todavía no está en marcha">
        Esta pantalla guarda la programación, pero el proceso que la ejecuta aún no está activo:
        hoy la medida de RIC se captura a mano desde{' '}
        <strong>Monitoreo → Medición de tanques</strong>. La <em>última ejecución</em> se llenará
        cuando ese proceso entre en funcionamiento.
      </Alert>

      {ric.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      ) : ric.error ? (
        <Alert variant="error" title="No se pudo leer la configuración">
          {mensajeDe(ric.error, 'La API no respondió.')}
        </Alert>
      ) : tanques.length === 0 ? (
        <Alert variant="warning" title="No hay tanques configurados">
          El controlador no reporta tanques (`pts_tanks` está vacío).
        </Alert>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {tanques.map((tanque) => (
            <TanqueTarjeta
              key={`${tanque.id_tanque}-${instantanea(tanque.config)}`}
              tanque={tanque}
              tipos={tipos}
              guardando={mutaciones.isPending && tanqueGuardando === tanque.id_tanque}
              onGuardar={async (idTanque, payload) => {
                setAviso(null);
                setTanqueGuardando(idTanque);

                try {
                  const datos = await mutaciones.mutateAsync({ idTanque, payload });
                  setAviso({ tono: 'success', texto: datos.mensaje });
                } finally {
                  setTanqueGuardando(null);
                }
              }}
            />
          ))}
        </div>
      )}

      <Alert variant="info">
        La configuración se guarda en la tabla del controlador{' '}
        (<code>pts_ric_automation_config</code>) y es la misma que leía el panel anterior.
      </Alert>
    </div>
  );
}
